import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  GoogleLogo,
  CalendarBlank,
  CheckCircle,
  WarningCircle,
  ArrowsClockwise,
  PlugsConnected,
  Plugs,
} from "@phosphor-icons/react";

/**
 * Owner-dashboard card for the Google Calendar two-way sync.
 *
 * States:
 *  - Not connected → "Connect" button + brief explainer.
 *  - Connected + needs_calendar_selection → calendar picker dropdown.
 *  - Connected + synced → status pill, last-sync timestamp, "Sync now"
 *    + "Disconnect" buttons, and a compact "Needs fix" inbox listing
 *    events Google has that failed validation (missing title /
 *    full address / start / end).
 *
 * Gated by feature flag `enable_google_calendar_sync` AND Pro
 * status — the backend `/status` endpoint reports `{enabled, reason}`
 * so we can render the right message without duplicating the gate
 * logic on the client.
 */
function MissingFieldsList({ missing }) {
  const labels = {
    title: "Title",
    address: "Address",
    address_incomplete: "Full street address (street + city/state or ZIP)",
    address_unrecognized: "Address (couldn't be located)",
    start: "Start time",
    end: "End time",
  };
  return (
    <ul className="mt-1 text-[11px] text-red-700 dark:text-red-300 flex flex-wrap gap-x-2 gap-y-0.5">
      {missing.map((m) => (
        <li key={m} className="inline-flex items-center gap-1">
          <WarningCircle weight="fill" className="h-3 w-3" />
          <span>{labels[m] || m}</span>
        </li>
      ))}
    </ul>
  );
}

export default function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [pending, setPending] = useState([]);

  const load = async () => {
    // Step 1: status is the authoritative source — only fall back to
    // "not connected" if /status itself fails. Subsequent calls
    // (/pending, /calendars) are non-fatal; if they 500 the user can
    // still see they're connected and disconnect/retry.
    let data;
    try {
      const r = await api.get("/integrations/google/status");
      data = r.data;
      setStatus(data);
    } catch (e) {
      setStatus({ enabled: true, connected: false });
      setLoading(false);
      return;
    }
    if (data?.connected) {
      try {
        const p = await api.get("/integrations/google/pending");
        setPending(p.data?.pending || []);
      } catch (_) {
        setPending([]);
      }
      if (data.needs_calendar_selection) {
        try {
          const c = await api.get("/integrations/google/calendars");
          setCalendars(c.data?.calendars || []);
        } catch (_) {
          setCalendars([]);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Surface the OAuth callback result if we just came back from
    // Google. URLSearchParams.get returns null when absent.
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get("gcal");
    if (gcal === "connected") {
      toast.success("Google Calendar connected.");
    } else if (gcal === "error") {
      toast.error(`Google Calendar connection failed: ${params.get("reason") || "unknown"}`);
    }
    if (gcal) {
      // Clean the URL so reload doesn't re-show the toast.
      params.delete("gcal");
      params.delete("reason");
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? `?${next}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !status) return null;

  // Feature disabled (admin kill switch)
  if (!status.enabled && status.reason === "feature_disabled") return null;

  // Free owners — upsell card
  if (!status.enabled && status.reason === "pro_required") {
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="gcal-card-upsell">
        <div className="flex items-center gap-2 mb-1">
          <GoogleLogo weight="duotone" size={20} className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">Connect Google Calendar</h2>
          <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[9px] uppercase tracking-wider font-bold">
            Pro
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Stop double-entering your schedule. Connect your Google Calendar and any event with a title, full address,
          and start/end time auto-publishes to TruckTrackr. Available on Pro.
        </p>
      </section>
    );
  }

  // Not connected — Pro owner, ready to start
  if (!status.connected) {
    const connect = async (kind) => {
      setBusy(true);
      try {
        const { data } = await api.post("/integrations/google/start", { calendar_kind: kind });
        window.location.href = data.authorization_url;
      } catch (e) {
        toast.error(formatApiError(e, "Couldn't start the Google connect flow"));
        setBusy(false);
      }
    };
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="gcal-card-connect">
        <div className="flex items-center gap-2 mb-1">
          <GoogleLogo weight="duotone" size={20} className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">Connect Google Calendar</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Add events to your Google Calendar and they show up on TruckTrackr automatically. Each event needs a
          <strong className="text-foreground"> title</strong>,
          <strong className="text-foreground"> full address</strong>, and
          <strong className="text-foreground"> start + end time</strong> — anything missing lands in your inbox below
          for a one-tap fix.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={() => connect("dedicated")}
            disabled={busy}
            data-testid="gcal-connect-dedicated"
            className="flex-1"
          >
            <PlugsConnected size={16} weight="bold" className="mr-1.5" />
            Create a dedicated "TruckTrackr Schedule" calendar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => connect("existing")}
            disabled={busy}
            data-testid="gcal-connect-existing"
            className="flex-1"
          >
            <CalendarBlank size={16} weight="bold" className="mr-1.5" />
            Pick an existing calendar
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-3">
          The dedicated option is the cleanest — TruckTrackr creates a new calendar in your Google account so personal
          events never leak onto the map.
        </div>
      </section>
    );
  }

  // Connected but still needs calendar selection (the "existing" flow)
  if (status.needs_calendar_selection) {
    const pick = async (calId) => {
      setBusy(true);
      try {
        await api.post("/integrations/google/select-calendar", { calendar_id: calId });
        toast.success("Calendar linked. First sync running…");
        await new Promise((r) => setTimeout(r, 1500));
        await load();
      } catch (e) {
        toast.error(formatApiError(e, "Couldn't select that calendar"));
      } finally {
        setBusy(false);
      }
    };
    const startOver = async () => {
      if (!window.confirm("Disconnect and start over? You can then choose 'Create a dedicated calendar' which doesn't require listing your existing calendars.")) return;
      setBusy(true);
      try {
        await api.post("/integrations/google/disconnect");
        toast.success("Disconnected. You can connect again with a dedicated calendar.");
        await load();
      } catch (e) {
        toast.error(formatApiError(e, "Disconnect failed"));
      } finally {
        setBusy(false);
      }
    };
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="gcal-card-select-calendar">
        <div className="flex items-center gap-2 mb-2">
          <GoogleLogo weight="duotone" size={20} className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">Pick a calendar</h2>
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] uppercase tracking-wider font-bold">
            <CheckCircle weight="fill" className="h-3 w-3" />
            Connected
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          You're connected as <strong className="text-foreground">{status.google_email}</strong>. Pick which Google Calendar should drive your TruckTrackr schedule. Only calendars you can write to are listed.
        </p>
        {calendars.length === 0 ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="text-sm font-semibold text-foreground mb-1">Couldn't load your calendars.</div>
            <p className="text-[12px] text-muted-foreground mb-3">
              Google's API rejected our request for the list of your calendars. The fastest fix is to disconnect and reconnect using the dedicated calendar option — TruckTrackr will create a new "TruckTrackr Schedule" calendar in your Google account for you.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={startOver} disabled={busy} data-testid="gcal-disconnect-fallback">
                <Plugs size={14} weight="bold" className="mr-1" />
                Disconnect &amp; start over
              </Button>
              <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="gcal-retry-calendars">
                <ArrowsClockwise size={14} weight="bold" className="mr-1" />
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {calendars.map((c) => (
              <li key={c.id} className="flex items-center gap-3 py-2">
                <CalendarBlank weight="duotone" className="h-5 w-5 text-primary flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {c.summary} {c.primary && <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-1">primary</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{c.time_zone}</div>
                </div>
                <Button size="sm" onClick={() => pick(c.id)} disabled={busy} data-testid={`gcal-pick-${c.id}`}>
                  Use this
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  // Fully connected → status + actions + inbox
  const lastSynced = status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : "Never";
  const syncOk = status.last_sync_status === "ok";
  const syncRevoked = status.last_sync_status === "revoked";

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google/sync-now");
      const s = data.stats || {};
      toast.success(`Synced. Published ${s.published || 0}, needs fix ${s.pending || 0}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Sync failed"));
    } finally {
      setBusy(false);
    }
  };

  const retryProvisionDedicated = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google/provision-dedicated");
      toast.success(`Dedicated calendar created: ${data.calendar_summary}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't create the dedicated calendar"));
    } finally {
      setBusy(false);
    }
  };

  const missingCalendar = !status.calendar_id;

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Synced events will be removed from your TruckTrackr schedule.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Disconnected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Disconnect failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="gcal-card-connected">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center flex-none">
          <GoogleLogo weight="duotone" size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-semibold text-lg tracking-tight">Google Calendar</h2>
            {syncRevoked ? (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-red-500/15 text-red-600 dark:text-red-300 text-[10px] uppercase tracking-wider font-bold">
                <WarningCircle weight="fill" className="h-3 w-3" />
                Reconnect required
              </span>
            ) : syncOk ? (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] uppercase tracking-wider font-bold">
                <CheckCircle weight="fill" className="h-3 w-3" />
                Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-muted text-muted-foreground text-[10px] uppercase tracking-wider font-bold">
                Pending first sync
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {status.google_email} · <span className="font-medium">{status.calendar_summary || status.calendar_id}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Last synced: {lastSynced}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button size="sm" onClick={syncNow} disabled={busy || missingCalendar} data-testid="gcal-sync-now">
            <ArrowsClockwise size={14} weight="bold" className="mr-1" />
            {busy ? "Syncing…" : "Sync now"}
          </Button>
          <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="gcal-disconnect">
            <Plugs size={14} weight="bold" className="mr-1" />
            Disconnect
          </Button>
        </div>
      </div>

      {/* Calendar wasn't provisioned — likely Calendar API was disabled at OAuth time */}
      {missingCalendar && (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="gcal-calendar-missing">
          <div className="flex items-center gap-2 mb-1">
            <WarningCircle weight="duotone" className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <div className="text-sm font-semibold text-foreground">No calendar linked yet</div>
          </div>
          <p className="text-[12px] text-muted-foreground mb-3">
            We couldn't create your "TruckTrackr Schedule" calendar in Google. This usually means the Google Calendar API isn't enabled in your Google Cloud project yet.
            {" "}<a href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">Enable it here</a>, wait ~1 minute, then click Retry.
          </p>
          <Button size="sm" onClick={retryProvisionDedicated} disabled={busy} data-testid="gcal-retry-provision">
            <ArrowsClockwise size={14} weight="bold" className="mr-1" />
            Retry calendar setup
          </Button>
        </div>
      )}
      {/* Needs-fix inbox */}
      {pending.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="gcal-pending-inbox">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle weight="duotone" className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <div className="text-xs font-semibold text-foreground">
              {pending.length} event{pending.length === 1 ? "" : "s"} need{pending.length === 1 ? "s" : ""} fixing
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            These events are in your Google Calendar but missing required info. Open the event in Google, add the
            missing fields, and the next sync (every 5 min) will publish it.
          </p>
          <ul className="divide-y divide-amber-500/10">
            {pending.slice(0, 6).map((p) => (
              <li key={p.gcal_event_id} className="py-2" data-testid={`gcal-pending-${p.gcal_event_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm truncate">{p.title || "(no title)"}</span>
                  {p.event_start && (
                    <span className="text-[11px] text-muted-foreground">
                      · {new Date(p.event_start).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                  {p.html_link && (
                    <a
                      href={p.html_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-[11px] font-semibold text-primary hover:underline"
                    >
                      Fix in Google →
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.delete(`/integrations/google/pending/${p.gcal_event_id}`);
                        setPending((prev) => prev.filter((x) => x.gcal_event_id !== p.gcal_event_id));
                        toast.success("Removed from the list.");
                      } catch {
                        toast.error("Couldn't remove it — try again.");
                      }
                    }}
                    className={`text-[11px] font-semibold text-muted-foreground hover:text-destructive ${p.html_link ? "" : "ml-auto"}`}
                    title="Remove from this list"
                    data-testid={`gcal-pending-dismiss-${p.gcal_event_id}`}
                  >
                    Remove
                  </button>
                </div>
                <MissingFieldsList missing={p.missing_fields || []} />
              </li>
            ))}
            {pending.length > 6 && (
              <li className="py-2 text-[11px] text-muted-foreground text-center">
                + {pending.length - 6} more
              </li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
